/**
 * READING DOCUMENTS (ADR-0020) — whether the firm's statements may be read
 * outside Leveri when Leveri cannot read them itself.
 *
 * The decision is the firm's, recorded as an act with a reason; whether this
 * viewer may make it is the server's answer (`viewerMayDecide`). It is shown
 * in two places: the firm's own page, and — when it matters — under a
 * statement Leveri could not read, so a person decides at the moment the
 * choice means something, not in a settings form nobody opens.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { DocumentReadingResponse } from "@leveri/contracts";
import { Button, Field, Notice, Reading, Section, SectionHead } from "../file/index.js";
import { call, failureMessage } from "../api.js";

/** "openai:gpt-6-luna" → "OpenAI". Who receives the document is named, plainly. */
export function readerCompany(id: string): string {
  const vendor = id.split(":")[0] ?? id;
  return ({ openai: "OpenAI", extend: "Extend", anthropic: "Anthropic" } as Record<string, string>)[vendor] ?? vendor;
}
const companies = (ids: readonly string[]) => [...new Set(ids.map(readerCompany))].join(" and ");

export function useDocumentReading() {
  const [state, setState] = useState<DocumentReadingResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setState(await call("GET", "/api/firm/document-reading", DocumentReadingResponse)); } catch (error) { setFailure(failureMessage(error)); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { state, setState, failure };
}

function DecideForm({ allow, onDecided }: { allow: boolean; onDecided: (next: DocumentReadingResponse) => void }) {
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function decide(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "").trim();
    setSaving(true);
    setFailure(null);
    try {
      onDecided(await call("POST", "/api/firm/document-reading", DocumentReadingResponse, { allowsOutsideReaders: allow, reason }));
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }
  return (
    <form onSubmit={decide} className="mt-4 grid gap-3" data-testid={allow ? "document-reading-allow" : "document-reading-stop"}>
      <Field label="Why" hint={allow ? "Filed with the decision — for example, that your clients agreed to it in their engagement letters." : "Filed with the decision."}>
        <textarea name="reason" rows={2} required disabled={saving}
          className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
      </Field>
      <div><Button type="submit" disabled={saving}>{allow ? "Allow for the firm" : "Stop outside reading"}</Button></div>
      {saving && <Reading>Recording the decision…</Reading>}
      {failure && <Notice>{failure}</Notice>}
    </form>
  );
}

/** The firm's page: what happens to a statement Leveri cannot read, who decided it, and why. */
export function DocumentReadingSection() {
  const { state, setState, failure } = useDocumentReading();
  const d = state?.documentReading ?? null;
  return (
    <Section>
      <SectionHead>Reading documents</SectionHead>
      {failure && <Notice>{failure}</Notice>}
      {!state && !failure && <Reading>Reading the firm's decision…</Reading>}
      {state && state.outsideReaders.length === 0 && (
        <p className="text-sm text-stone" data-testid="document-reading-none">
          Leveri reads statements itself. No outside reader is set up here, so every document stays inside Leveri.
        </p>
      )}
      {state && state.outsideReaders.length > 0 && d && (
        <div className="grid gap-2 text-sm text-ink" data-testid="document-reading-state">
          {d.allowsOutsideReaders ? (
            <p>
              Statements Leveri cannot read itself are read by {companies(state.outsideReaders)}, then checked by Leveri — every figure and date
              against the page, and the statement's own arithmetic to the cent — before anything is used.
            </p>
          ) : (
            <p>
              Statements Leveri cannot read itself — scans, unusual layouts — are returned to you unread. Your firm can allow
              {" "}{companies(state.outsideReaders)} to read them; Leveri checks every figure and date before anything is used.
            </p>
          )}
          {d.decidedBy && (
            <p className="text-stone">
              {d.allowsOutsideReaders ? "Allowed" : "Stopped"} by {d.decidedBy.displayName}
              {d.decidedAt ? ` on ${new Date(d.decidedAt).toLocaleDateString()}` : ""}: “{d.reason}”
            </p>
          )}
          {state.viewerMayDecide
            ? <DecideForm allow={!d.allowsOutsideReaders} onDecided={setState} />
            : <p className="text-stone">A member who can grant authority in the firm can change this.</p>}
        </div>
      )}
    </Section>
  );
}

/**
 * Under a statement Leveri could not read: the choice, offered where it
 * matters — only when an outside reader is set up and the firm has not
 * allowed it. After allowing, the file is enclosed again to be read.
 */
export function OutsideReadingOffer() {
  const { state, setState } = useDocumentReading();
  if (!state || state.outsideReaders.length === 0) return null;
  if (state.documentReading.allowsOutsideReaders) {
    return <p className="mt-1 text-[13px] text-stone" data-testid="outside-reading-allowed">Outside reading is allowed for the firm — enclose the file again and Leveri will have it read.</p>;
  }
  return (
    <div className="mt-1 text-[13px] text-stone" data-testid="outside-reading-offer">
      <p>
        Leveri could not read this itself. Your firm can allow {companies(state.outsideReaders)} to read statements like it — Leveri checks every
        figure and date before anything is used.
      </p>
      {state.viewerMayDecide
        ? <DecideForm allow onDecided={setState} />
        : <p className="mt-1">A member who can grant authority in the firm can allow it.</p>}
    </div>
  );
}
